// FloatingChat.tsx

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Textarea, Button, Box, Flex, Text } from '@chakra-ui/react';
import { FaWhatsapp } from 'react-icons/fa';
import { motion, useAnimation } from 'framer-motion';
import { useStore } from '@contexts/store';
import Image from 'next/image';
import { FEATURE_KEYS } from '@utils/feature-keys';
import { useAccessControl } from '@framework/use-shop-features';

const uploadCareCDNBase: string = 'https://ucarecdn.com/';

const FloatingChat: React.FC = () => {
  const { storeData: shop } = useStore();
  const [showChat, setShowChat] = useState<boolean>(false);
  const [canShowBubble, setCanShowBubble] = useState<boolean>(false);
  const [whatsAppNumber, setWhatsAppNumber] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const floatingButtonControls = useAnimation();
  const submitButtonControls = useAnimation();

  const {hasFeature} = useAccessControl(shop?._id)

  useEffect(() => {
    if (shop?._id) {
      setMessage(`Hi ${shop.name}, I have a question`);
      if (hasFeature(FEATURE_KEYS.BUY_OVER_WHATSAPP) && shop?.config?.storefront?.showWhatsAppBubble && shop?.whatsapp_number?.length >= 10) {
        const cleanPhoneNumber = shop?.whatsapp_number?.replace(/\s/g, '');
        setCanShowBubble(true);
        setWhatsAppNumber(cleanPhoneNumber);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop]);

  const logo = useMemo(() => {
    return shop?.logo_uuid ? `${uploadCareCDNBase}${shop?.logo_uuid}/` : '';
  }, [shop]);

  const handleChatToggle = () => {
    setShowChat(!showChat);
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    const whatsappMessage = encodeURIComponent(message.trim());
    const whatsappLink = `https://wa.me/${whatsAppNumber}?text=${whatsappMessage}`;

    window.open(whatsappLink, '_blank');

    setShowChat(false);
  };

  const handleEnterPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  useEffect(() => {
    floatingButtonControls.start({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: Math.random() * 2,
      },
    });
  }, [floatingButtonControls]);

  useEffect(() => {
    if (canShowBubble && showChat && message.trim() !== '') {
      submitButtonControls.start({ opacity: 1, y: 0 });
    } else {
      submitButtonControls.start({ opacity: 0, y: 20 });
    }
  }, [canShowBubble, showChat, message, submitButtonControls]);

  // if (!canShowBubble) return null;

  return (
    <>
      {canShowBubble ? (
        <motion.div
          // initial={{ opacity: 0, y: 20 }}
          // animate={floatingButtonControls}
          whileHover={{ scale: 1.3 }}
          onClick={handleChatToggle}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            borderRadius: '50%',
            padding: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)',
            cursor: 'pointer',
            backgroundColor: showChat ? '#e74c3c' : '#25d366',
            zIndex: 9999,
          }}
        >
          <FaWhatsapp size={40} color="#fff" />
        </motion.div>
      ) :  null}

      {showChat && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          style={{
            position: 'fixed',
            bottom: '80px',
            right: '20px',
            width: '300px',
            padding: '10px',
            background: '#fff',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)',
            zIndex: 9999,
          }}
        >
          <Box mb={4} borderBottom="1px solid #eee">
            <Flex alignItems="left">
              <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} style={{ marginRight: '8px' }}>
                <Image
                  src={logo}
                  alt="WhatsApp"
                  className="logo"
                  height={25}
                  width={25}
                  style={{
                    borderRadius: '50%',
                  }}
                />
              </motion.div>
              <motion.h3
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                style={{ fontWeight: 'bold', color: '#25d366', marginBottom: '8px' }}
              >
                {shop.name}
              </motion.h3>
            </Flex>
          </Box>
          <form onSubmit={handleSendMessage}>
            <Flex bg="gray.100" direction="column" p="2" gap={2} alignSelf="flex-start" borderRadius="md" mb={4} fontSize={12}>
              <Flex bg="white" p="2" direction="column" borderRadius="md" maxW="70%">
                <Text h="3" fontSize={8} fontWeight="bold" color="#007bfc" mr="2">
                  {shop.name}
                </Text>
                <Text>Hello 👋🏼, Welcome to {shop.name}</Text>
              </Flex>
              <Box bg="green.100" p="2" borderRadius="md" ml="auto">
                <Text>{''}</Text>
              </Box>
            </Flex>
            <Textarea
              placeholder="Type your message..."
              size="sm"
              resize="none"
              mb={4}
              ref={textareaRef}
              value={message}
              onChange={(e: { target: { value: any } }) => setMessage(e.target.value)}
              onKeyDown={handleEnterPress}
            />
            <Flex justify="center" mt="auto">
              <Button colorScheme="green" leftIcon={<FaWhatsapp />} type="submit">
                WhatsApp Us
              </Button>
            </Flex>
          </form>
          <Flex fontSize="small" align="center" justify="center" mt="2">
            <Box w="3" h="3" bg="green.200" borderRadius="50%" mr="2" />
            <Text color="green.500">Online</Text>
          </Flex>
        </motion.div>
      )}
    </>
  );
};

export default FloatingChat;
